import React from 'react';
import { Check, CheckCircle, Clock, User, X, Ban } from 'lucide-react';
import { formatRole } from './ApprovalTimeline';

// ─── DS class mapping (design-system-reference.md → Horizontal Stepper System) ───
// Step circle        : w-10 h-10 rounded-full transition-all duration-200
// Completed/current  : employee theme → bg-blue-800 text-white shadow-sm
// Current pulse      : ring-4 ring-blue-200 animate-pulse (WorkerLeaveModal pattern)
// Rejected           : Status Taxonomy → bg-red-600 text-white shadow-sm
// Upcoming           : bg-gray-200 text-gray-500
// Connector          : pill → flex-1 h-1 rounded-full, blue-800 when traversed
// Section container  : bg-gray-50 border border-gray-200 rounded-xl p-4
// Section heading    : text-xs font-semibold text-gray-500 uppercase tracking-wide
// ─────────────────────────────────────────────────────────────────────────────────

const CIRCLE_CLASSES = {
  completed: 'bg-blue-800 text-white shadow-sm',
  current: 'bg-blue-800 text-white shadow-sm ring-4 ring-blue-200 animate-pulse',
  rejected: 'bg-red-600 text-white shadow-sm',
  upcoming: 'bg-gray-200 text-gray-500',
};

const LABEL_CLASSES = {
  completed: 'text-blue-800 font-medium',
  current: 'text-blue-800 font-semibold',
  rejected: 'text-red-600 font-semibold',
  upcoming: 'text-gray-500',
};

// Sick leave is never approved through the RO/Trade Leader workflow chain —
// HR verifies the medical certificate, which approves the application.
const SICK_LEAVE_TYPE_NAMES = ['outpatient sick leave', 'hospitalisation leave'];

/**
 * Horizontal at-a-glance stepper showing where a leave application sits
 * in its multi-level approval chain: one step per workflow level plus a
 * terminal outcome step (Approved / Rejected / Cancelled).
 *
 * Sick leave (OSL / Hospitalisation) renders an HR medical-certificate
 * verification flow instead of the workflow hierarchy.
 *
 * Derives everything from the `leave` prop (same API as ApprovalTimeline):
 *   approval_hierarchy, approval_history, current_approval_level,
 *   current_approver, leave_status.
 *
 * Consumers:
 *   - Leave.js            staff/worker detail modal (above ApprovalTimeline)
 *   - WorkerLeaveModal    Step2ApprovalProgress (above the level cards)
 */
const ApprovalStepper = ({ leave }) => {
  const hierarchy = leave?.approval_hierarchy || [];
  const leaveTypeName = (leave?.leave_type?.leave_type_name || leave?.leave_type_name || '').toLowerCase();
  const isSickLeave = SICK_LEAVE_TYPE_NAMES.includes(leaveTypeName);

  if (!leave || leave.leave_status === 'draft') return null;
  if (!isSickLeave && hierarchy.length === 0) return null;

  const status = leave.leave_status;
  const history = leave.approval_history || [];
  const isApproved = status === 'approved' || status === 'auto_approved';
  const isRejected = status === 'rejected';
  const isCancelled = status === 'cancelled';
  const isPending = status === 'submitted' || status === 'pending';
  const currentLevel = leave.current_approval_level || 1;

  const getLevelState = (level) => {
    if (history.some(h => h.level === level && h.action === 'rejected')) return 'rejected';
    if (history.some(h => h.level === level && (h.action === 'approved' || h.action === 'auto_skipped'))) return 'completed';
    if (isApproved) return 'completed';
    // Passed without an approval entry (auto-advanced / escalated past it)
    if (isPending && level < currentLevel) return 'completed';
    if (isPending && level === currentLevel) return 'current';
    return 'upcoming';
  };

  let steps;
  let pendingWith;

  if (isSickLeave) {
    const verificationState = isApproved ? 'completed'
      : isRejected ? 'rejected'
      : isPending ? 'current'
      : 'upcoming';
    steps = [
      {
        key: 'submitted',
        state: 'completed',
        label: 'Submitted',
        title: 'Application submitted',
        icon: Check,
      },
      {
        key: 'hr-verification',
        state: verificationState,
        label: 'HR Verification',
        title: 'HR verifies the medical certificate',
        icon: verificationState === 'completed' ? Check
          : verificationState === 'current' ? Clock
          : verificationState === 'rejected' ? X
          : User,
      },
      {
        key: 'outcome',
        state: isApproved ? 'completed' : isRejected ? 'rejected' : 'upcoming',
        label: isRejected ? 'Rejected' : isCancelled ? 'Cancelled' : 'Verified',
        title: isRejected ? 'Application rejected' : isCancelled ? 'Application cancelled' : 'Medical certificate verified',
        icon: isRejected ? X : isCancelled ? Ban : CheckCircle,
      },
    ];
    pendingWith = isPending ? 'HR (medical certificate verification)' : null;
  } else {
    steps = hierarchy.map((entry) => {
      const state = getLevelState(entry.level);
      const approverLabel = [entry.approver_name, entry.approver_staff_id].filter(Boolean).join(' · ');
      return {
        key: `level-${entry.level}`,
        state,
        label: formatRole(entry.role),
        title: approverLabel ? `${formatRole(entry.role)}: ${approverLabel}` : formatRole(entry.role),
        icon: state === 'completed' ? Check
          : state === 'current' ? Clock
          : state === 'rejected' ? X
          : User,
      };
    });

    // Terminal outcome step
    steps.push({
      key: 'outcome',
      state: isApproved ? 'completed' : isRejected ? 'rejected' : 'upcoming',
      label: isRejected ? 'Rejected' : isCancelled ? 'Cancelled' : 'Approved',
      title: isRejected ? 'Application rejected' : isCancelled ? 'Application cancelled' : 'Final approval',
      icon: isRejected ? X : isCancelled ? Ban : CheckCircle,
    });

    // Name of the approver the application is currently waiting on
    const pendingEntry = hierarchy.find(h => h.level === currentLevel);
    pendingWith = isPending
      ? (leave.current_approver?.name
          || pendingEntry?.approver_name
          || formatRole(pendingEntry?.role))
      : null;
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
        {isSickLeave ? 'Verification Progress' : 'Approval Progress'}
      </h4>
      <div className="flex items-start">
        {steps.map((step, index) => {
          const Icon = step.icon;
          const circleClass = CIRCLE_CLASSES[step.state];
          const labelClass = LABEL_CLASSES[step.state];
          return (
            <React.Fragment key={step.key}>
              <div className="flex flex-col items-center min-w-0">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${circleClass}`}
                  title={step.title}
                >
                  <Icon className="w-5 h-5" />
                </div>
                <span className={`text-xs mt-2 text-center max-w-[60px] leading-tight ${labelClass}`}>
                  {step.label}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div
                  className={`flex-1 h-1 rounded-full mx-2 mt-[18px] transition-all duration-200 ${
                    step.state === 'completed' ? 'bg-blue-800' : 'bg-gray-200'
                  }`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
      {pendingWith && (
        <p className="text-xs text-gray-500 mt-3 text-center">
          Pending with: <span className="font-medium text-gray-700">{pendingWith}</span>
        </p>
      )}
    </div>
  );
};

export default ApprovalStepper;
